import { ChevronDownIcon, CollectionIcon, GiftIcon, GlobeIcon, HandshakeIcon, PassIcon } from "@xola/icons";
import { Checkbox, Tag } from "@xola/ui-kit";
import React, { useCallback, useEffect, useRef } from "react";
import { ProductObject } from "types";
import { MultiSelectPopover } from "../MultiSelectPopover";
import { useVisibility } from "@/hooks/useVisibility";

export interface ProductObjectFilter {
    productObject: ProductObject;
    label: string;
    count: number;
    enabled: boolean;
}

interface ProductObjectFilterProps {
    productObjectFilters: ProductObjectFilter[];
    isProductObjectDisabled?: (productObject: ProductObject) => boolean;
    onProductObjectFiltersChange: (filters: ProductObjectFilter[]) => void;
}

export const ProductObjectFilterDropdown = ({
    productObjectFilters,
    isProductObjectDisabled,
    onProductObjectFiltersChange,
}: ProductObjectFilterProps) => {
    const [isDropdownVisible, { toggle: toggleDropdown, hide: hideDropdown }] = useVisibility(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleProductObjectFilter = (productObject: ProductObject) => {
        const updatedProductObject = productObjectFilters.map((productObjectFilter) =>
            productObjectFilter.productObject === productObject
                ? { ...productObjectFilter, enabled: !productObjectFilter.enabled }
                : productObjectFilter,
        );
        onProductObjectFiltersChange(updatedProductObject);
    };

    const handleSelectAllObject = () => {
        const allSelectableFilters = productObjectFilters.filter(
            (productObjectFilter) => !isProductObjectDisabled?.(productObjectFilter.productObject),
        );
        const allEnabled = allSelectableFilters.length > 0 && allSelectableFilters.every((f) => f.enabled);
        onProductObjectFiltersChange(
            productObjectFilters.map((productObjectFilter) =>
                isProductObjectDisabled?.(productObjectFilter.productObject)
                    ? productObjectFilter
                    : { ...productObjectFilter, enabled: !allEnabled },
            ),
        );
    };

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                hideDropdown();
            }
        },
        [hideDropdown],
    );

    useEffect(() => {
        if (isDropdownVisible) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [isDropdownVisible, handleClickOutside]);

    if (productObjectFilters.length === 0) {
        return null;
    }

    return (
        <>
            <div ref={dropdownRef} className="relative">
                <div
                    className="flex cursor-pointer items-center rounded border border-gray bg-white px-4 py-2"
                    onClick={toggleDropdown}
                >
                    <span className="text-sm text-black">Product types</span>
                    <span className="ml-auto">
                        <ChevronDownIcon />
                    </span>
                </div>

                {isDropdownVisible && (
                    <div className="absolute z-10 w-full rounded border border-gray-lighter bg-white pt-1 shadow-lg">
                        <MultiSelectPopover.Header>
                            <div className="flex flex-row space-x-2">
                                <Checkbox
                                    checked={
                                        productObjectFilters.some(
                                            (productObjectFilter) =>
                                                !isProductObjectDisabled?.(productObjectFilter.productObject),
                                        ) &&
                                        productObjectFilters
                                            .filter(
                                                (productObjectFilter) =>
                                                    !isProductObjectDisabled?.(productObjectFilter.productObject),
                                            )
                                            .every((f) => f.enabled)
                                    }
                                    onChange={handleSelectAllObject}
                                />
                                <span className="text-sm">Select All</span>
                            </div>
                        </MultiSelectPopover.Header>

                        <div>
                            {productObjectFilters.map((productObjectFilter) => (
                                <MultiSelectPopover.Item
                                    key={productObjectFilter.productObject}
                                    className="border-b-dotted border-b border-gray-light bg-white"
                                >
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            checked={productObjectFilter.enabled}
                                            disabled={isProductObjectDisabled?.(productObjectFilter.productObject)}
                                            onChange={() =>
                                                handleProductObjectFilter(productObjectFilter.productObject)
                                            }
                                        />
                                        {getProductObjectIcon(productObjectFilter.productObject)}
                                        <span className="text-sm font-bold text-black">
                                            {productObjectFilter.label}
                                        </span>
                                    </div>
                                </MultiSelectPopover.Item>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {productObjectFilters.some((productObjectFilter) => productObjectFilter.enabled) && (
                <div className="flex flex-wrap gap-2">
                    {productObjectFilters
                        .filter((productObjectFilter) => productObjectFilter.enabled)
                        .map((productObjectFilter) => (
                            <Tag key={productObjectFilter.productObject}>
                                <span>{productObjectFilter.label}:</span>
                                <span className="ml-1">{productObjectFilter.count}</span>
                            </Tag>
                        ))}
                </div>
            )}
        </>
    );
};

/**
 * Returns the appropriate icon component for a given product type.
 */
export const getProductObjectIcon = (productObject: ProductObject) => {
    switch (productObject) {
        case ProductObject.EXPERIENCE:
            return <GlobeIcon />;
        case ProductObject.PACKAGE:
        case ProductObject.PARTNER_PACKAGE:
            return <HandshakeIcon />;
        case ProductObject.GIFT_OFFER:
            return <GiftIcon />;
        case ProductObject.MEMBERSHIP:
            return <PassIcon />;
        case ProductObject.COLLECTION:
            return <CollectionIcon />;
        default:
            return <GlobeIcon />;
    }
};
